import { jsPDF } from "jspdf";
import utils from "./Utils";
import 'jspdf-autotable'

const smallIdFor = (id) => {
  return id.slice(0,8)
}

const exportAndDownloadCsv = (recordStatement, account, initialBalance) => {
  let getDefaultHeaders = () => {
    return [
      'ID',
      'DATE',
      'SUBJECT_ID',
      'NAME',
      'DOCUMENT_NUMBER',
      'DOCUMENT_TYPE',
      'KIND',
      'VALUE',
      'CURRENCY',
      'EXCHANGED_VALUE',
      'EXCHANGE_CURRENCY',
      'EFFECTIVE VALUE',
      'BALANCE',
      'EXCHANGE RATE',
      'EXCHANGE SPOT',
      'EXCHANGE ID',
      'WIRE ID'
    ]
  }

  let getCashOutHeaders = () => {
    return [
      'ID',
      'DATE',
      'KIND',
      'VALUE',
      'EFFECTIVE VALUE',
      'BALANCE',
      'CURRENCY',
      'OBJECT_ID',
      'DESCRIPTION',
      'OBJECT_TYPE',
    ]
  }

  const is_facilita_cash_out = account.flow_type == "facilita_cash_out" ? true : false
  const header_line = is_facilita_cash_out ? getCashOutHeaders() : getDefaultHeaders()

  const parseValue = (value, positive) => {
    if (value === null) { return '' }
    if (typeof(value) === 'undefined') { return '' }
    if (positive) { return Number(value) }
    return -Math.abs(Number(value))
  }

  const parseDate = (d) => {
    const splitted = d.split('T')
    const date = splitted[0]
    const time = splitted[1].split('Z')[0]
    return date + ' ' + time
  }
  
  let balance = Number(initialBalance) || parseCurrency(account.value)

  const rows = recordStatement.reduce((rows, record) => {
    let row = []
    const date = parseDate(record.inserted_at)

    let value, exchanged_value, effective_value
    if (record.kind === 'CREDIT') {
      value = parseValue(record.value, true);
      exchanged_value = parseValue(record.exchanged_value, true);
      effective_value = parseValue(record.exchanged_value, true)
    } else {
      value = parseValue(record.value, false);
      exchanged_value = parseValue(record.exchanged_value, false);
      effective_value = parseValue(record.value, false)
    }


    if (is_facilita_cash_out) {
      row.push(record.id)
      row.push(date)
      row.push(record.kind)
      row.push(format_value(value))
      row.push(format_value(effective_value))
      row.push(format_value(balance))
      row.push(record.currency)
      row.push(record.object_id)
      row.push(record.description)
      row.push(record.object_type == "scheduled_bank_transaction" ? "transaction" : "wire")
    } else {
      row.push(record.id)
      row.push(date)
      row.push(record.subject_id)
      row.push(record.social_name)
      row.push(record.document_number)
      row.push(record.document_type)
      row.push(record.kind)
      row.push(format_value(value))
      row.push(record.currency)
      row.push(format_value(exchanged_value))
      row.push(record.exchange_currency)
      row.push(format_value(effective_value))
      row.push(format_value(balance))
      row.push(parseValue(record.exchange_rate, true))
      row.push(parseValue(record.exchange_rate_spot, true))
      row.push(record.exchange_id)
      row.push(record.wire_id)
    }

    balance -= Number(effective_value)

    rows.push(row)
    return rows
  }, [header_line])

  const now = new Date(Date.now())
  const day_now = now.toISOString().split('T')[0].split('-').join('')
  const time_now = now.toISOString().split('T')[1].split('.')[0].split(':').join('')
  const timestamp = `${day_now}-${time_now}`
  const account_currency = account.currency
  const account_small_id = smallIdFor(account.id)
  const filename = `account_statement_report_${account_currency}_${account_small_id}_${timestamp}.csv`

  utils.exportToCsv(filename, rows.filter(row => row !== undefined), ',')
}

const format_value = (value) => {
  if (value) { return value.toFixed(2) }
  return value
}

const exportAndDownloadTransactionsCsv = (recordStatement) => {
  let getDefaultHeaders = () => {
    return [
      'ID',
      'DATE',
      'SUBJECT_ID',
      'NAME',
      'DOCUMENT_NUMBER',
      'DOCUMENT_TYPE',
      'VALUE',
      'CURRENCY',
      'EXCHANGED_VALUE',
      'EXCHANGE_CURRENCY',
      'WIRE ID',
      'CLEARED',
      'ESTIMATED_VALUE_UNTIL_EXCHANGE',
      'EXCHANGE_UNDER_REQUEST',
      'FOR_EXCHANGE',
      'FROM_BANK_ACCOUNT_ID',
      'FROM_CREDIT_CARD_ID',
      'META',
      'STATUS',
      'SUBJECT_IS_RECEIVER',
      'TO_BANK_ACCOUNT_ID'
    ]
  }

  const parseValue = (str, positive) => {
    if (str === null) { return '' }
    if (typeof(str) === 'undefined') { return '' }
    if (positive) { return String(str) }
    return '-' + String(str)
  }

  const parseDate = (d) => {
    const splitted = d.split('T')
    const date = splitted[0]
    const time = splitted[1].split('Z')[0]
    return date + ' ' + time
  }

  const rows = recordStatement.reduce((rows, record) => {
    let row = []
    const date = parseDate(record.inserted_at)

    let value, currency, exchanged_value, exchange_currency
    if (record.kind === 'CREDIT') {
      value = parseValue(record.exchanged_value, true)
      currency = record.exchange_currency
      exchanged_value = parseValue(record.value, true)
      exchange_currency = record.currency
    } else {
      value = parseValue(record.value, false)
      currency = record.currency
      exchanged_value = parseValue(record.exchanged_value, false)
      exchange_currency = record.exchange_currency
    }

    let wire_id
    if (record && record.bank_transaction) {
      wire_id = record.bank_transaction.wire_id
    }

    row.push(record.id)
    row.push(date)
    row.push(record.subject_id)
    row.push(record.source_name)
    row.push(record.source_document_number)
    row.push(record.source_document_type)
    row.push(value)
    row.push(currency)
    row.push(exchanged_value)
    row.push(exchange_currency)
    row.push(wire_id)
    row.push(record.cleared)
    row.push(record.estimated_value_until_exchange)
    row.push(record.exchange_under_request)
    row.push(record.for_exchange)
    row.push(record.from_bank_account_id)
    row.push(record.from_credit_card_id)
    row.push(record.meta)
    row.push(record.status)
    row.push(record.subject_is_receiver)
    row.push(record.to_bank_account_id)

    rows.push(row)
    return rows
  }, [getDefaultHeaders()])

  const now = new Date(Date.now())
  const day_now = now.toISOString().split('T')[0].split('-').join('')
  const time_now = now.toISOString().split('T')[1].split('.')[0].split(':').join('')
  const timestamp = `${day_now}-${time_now}`
  const filename = `accounts_transactions_report_${timestamp}.csv`

  utils.exportToCsv(filename, rows.filter(row => row !== undefined), ',')
}

const exchangeRate = (statement) => {
  if (!statement.exchange_rate) { return 'N/A' }
  return parseFloat(statement.exchange_rate).toFixed(2)
}

const statementValue = (statement) => {
  let value = 0
  let currency = ''
  if (statement.kind === 'DEBIT') {
    value = statement.value * -1
    currency = statement.currency
  }
  else if (statement.kind === 'FEE') {
    value = statement.value * -1
    currency = statement.currency
  } else {
    value = statement.exchanged_value
    currency = statement.exchange_currency
  }
  return utils.currencyToLocal(value, currency)
}

const statementExchangedValue = (statement) =>  {
  let value = 0
  let currency = ''
  if (statement.kind === 'DEBIT') {
    value = statement.exchanged_value * -1
    currency = statement.exchange_currency
  } else {
    value = statement.value
    currency = statement.currency
  }
  return utils.currencyToLocal(value, currency)
}

const hasExchangeValue = (statement) => {
  if (!statement.exchanged_value) { return false }
  return statement.currency != statement.exchange_currency
}

const exportAndDownloadPdf = (recordStatement, statementAccount, lastBalance, filterStartDate, filterEndDate, hasPreviousPage, hasNextPage) => {
  const doc = new jsPDF()

  let getCashOutHeaders = ['DATE', 'DESCRIPTION', 'TRANSACTION/WIRE ID', 'VALUE']
  let getDefaultHeaders = ['DATE','TRANSACTION ID','NAME','TYPE','OPERATION','RATE','VALUE']

  const header_line = statementAccount.flow_type == "facilita_cash_out" ? getCashOutHeaders : getDefaultHeaders

  const getAccountInfos = (statementAccount) => {
    if (!statementAccount.branch_number || !statementAccount.account_number) {
      return ''
    } else {
      return `Branch: ${statementAccount.branch_number} Account number: ${statementAccount.account_number}`
    }
  }

  const getStartDate = (record) => {
    if (filterStartDate) {
      return utils.dbDateTimeToHumanDate(filterStartDate)
    } else {
      return utils.dbDateTimeToHumanDate(record[record.length - 1].inserted_at)
    }
  }

  const getEndDate = (record) => {
    if (filterEndDate) {
      return utils.dbDateTimeToHumanDate(filterEndDate)
    } else {
      return utils.dbDateTimeToHumanDate(record[0].inserted_at)
    }
  }

  let totalPages = 'total_pages'
  const account = statementAccount
  const current_date = new Date().toLocaleString()
  const header_line_a = `Report generated at ${current_date}`
  const header_line_b = `for internal account ${account.currency}/${smallIdFor(account.id)}`
  const header_line_c = `Client: ${statementAccount.company_social_name}`
  const header_line_d = getAccountInfos(statementAccount)
  const header_line_e = `Start date: ` + getStartDate(recordStatement)
  const header_line_f = `End date: ` + getEndDate(recordStatement)

  let getDefaultStatement = (record) => {
    let row = []
    row.push(`${utils.dbDateTimeToHuman(record.inserted_at).split(' ')[0]} ${utils.dbDateTimeToHuman(record.inserted_at).split(' ')[1]}`)
    row.push(record.id)
    row.push(`${record.social_name}\n${record.document_type.toUpperCase()} ${utils.mask(record.document_type, record.document_number)}`)
    row.push(record.kind)
    row.push(`${record.currency}/${record.exchange_currency}`)
    row.push(exchangeRate(record))
    row.push(statementValue(record) + `${hasExchangeValue(record) ? `\n` + statementExchangedValue(record) : ''}`)
    return row
  }

  let getCashOutStatement = (record) => {
    let completeFeeType = (type) => type == "WIRE" ? "INTERNATIONAL WIRE" : type

    let row = []
    row.push(`${utils.dbDateTimeToHuman(record.inserted_at).split(' ')[0]} ${utils.dbDateTimeToHuman(record.inserted_at).split(' ')[1]}`)
    row.push(`${record.kind} From ${record.kind == 'FEE' ? completeFeeType(record.description) : record.social_name}`)
    row.push(`ID ${record.kind == 'FEE' ? record.object_id : record.id}`)
    row.push(statementValue(record) + `${hasExchangeValue(record) ? `\n` + statementExchangedValue(record) : ''}`)
    return row
  }

  const builtStatement = buildStatementRowsWithBalance(recordStatement, statementAccount, lastBalance, hasPreviousPage, hasNextPage)
  const rows = builtStatement.rows

  doc.autoTable({
    head: [header_line],
    body: rows,
    margin: {top: 40, left: 5, right: 5},
    columnStyles: {fontSize: 5},
    styles: {fontSize: 6},
    startY: 40,
    didParseCell: function (table) {
      if (table.section === 'head') {
        table.cell.styles.fillColor  = '#27DABC'
      }
    },
    willDrawCell: (data) => {
      if (['head', 'body'].includes(data.section) && data.column.dataKey == 6) {
        data.cell.styles.halign = 'right';
      }
      if (['head', 'body'].includes(data.section) && [5,4,3].includes(data.column.dataKey) ) {
        data.cell.styles.halign = 'center';
      }
    },
    didDrawPage: function(data) {
      // Header
      doc.addImage("static/facilitapay_logo.jpg", 'PNG', 5, 5, 30, 10)
      doc.setFontSize(10)
      doc.setTextColor(10, 45, 74);
      const right_margin = doc.getPageWidth() - data.settings.margin.right
      const header_line_a_dimentions = doc.getTextDimensions(header_line_a);
      const header_line_b_dimentions = doc.getTextDimensions(header_line_b);
      const header_line_e_dimentions = doc.getTextDimensions(header_line_e);
      const header_line_f_dimentions = doc.getTextDimensions(header_line_f);
      doc.text(header_line_a, right_margin - header_line_a_dimentions.w, 10);
      doc.text(header_line_b, right_margin - header_line_b_dimentions.w, 15);
      doc.text(header_line_c, 5, 30);
      doc.text(header_line_d, 5, 35);
      doc.text(header_line_e, right_margin - header_line_e_dimentions.w, 30);
      doc.text(header_line_f, right_margin - header_line_f_dimentions.w, 35);

      // Footer
      let page = 'page ' + doc.internal.getNumberOfPages() + ' of ' + totalPages
      doc.setFontSize(7)

      let pageSize = doc.internal.pageSize
      let pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
      doc.text(page, data.settings.margin.left, pageHeight - 10)
    },
  });
  doc.putTotalPages(totalPages)
  const now = new Date(Date.now())
  const day_now = now.toISOString().split('T')[0].split('-').join('')
  const time_now = now.toISOString().split('T')[1].split('.')[0].split(':').join('')
  const timestamp = `${day_now}-${time_now}`
  const account_currency = statementAccount.currency
  const account_small_id = smallIdFor(statementAccount.id)
  const filename = `account_statement_report_${account_currency}_${account_small_id}_${timestamp}.pdf`
  doc.save(filename)
}

const getDefaultStatement = (record) => {
  let row = []
  row.push(`${utils.dbDateTimeToHuman(record.inserted_at).split(' ')[0]} ${utils.dbDateTimeToHuman(record.inserted_at).split(' ')[1]}`)
  row.push(record.id)
  row.push(`${record.social_name}\n${record.document_type.toUpperCase()} ${utils.mask(record.document_type, record.document_number)}`)
  row.push(record.kind)
  row.push(`${record.currency}/${record.exchange_currency}`)
  row.push(exchangeRate(record))
  row.push(statementValue(record) + `${hasExchangeValue(record) ? `\n` + statementExchangedValue(record) : ''}`)
  return row
}

const getCashOutStatement = (record) => {
  let completeFeeType = (type) => type == "WIRE" ? "INTERNATIONAL WIRE" : type

  let row = []
  row.push(`${utils.dbDateTimeToHuman(record.inserted_at).split(' ')[0]} ${utils.dbDateTimeToHuman(record.inserted_at).split(' ')[1]}`)
  row.push(`${record.kind} From ${record.kind == 'FEE' ? completeFeeType(record.description) : record.social_name}`)
  row.push(`ID ${record.kind == 'FEE' ? record.object_id : record.id}`)
  row.push(statementValue(record) + `${hasExchangeValue(record) ? `\n` + statementExchangedValue(record) : ''}`)
  return row
}

const buildStatementRowsWithBalance = (recordStatement, statementAccount, statementBalanceValue, hasPreviousPage, hasNextPage) => {
  const getStatementRow = statementAccount.flow_type === "facilita_cash_out"
    ? getCashOutStatement
    : getDefaultStatement

  let balance_final = parseCurrency(statementBalanceValue)
  let balance = balance_final
  const rows = []

  const getRecordDay = (record) => utils.dbDateTimeToHumanDate(getStatementRow(record)[0])

  recordStatement.forEach((record, index) => {
    const row = getStatementRow(record)
    const recordDay = getRecordDay(record)

    const value = record.kind === 'CREDIT' ? Number(record.exchanged_value) : -Number(record.value)
    balance -= value

    rows.push(row)

    const nextRecord = recordStatement[index + 1]
    const nextDay = nextRecord ? getRecordDay(nextRecord) : null
    const isLastRecord = index === recordStatement.length - 1

    if (isLastRecord || recordDay !== nextDay) {
      let balanceName;

      if (isLastRecord) {
        balanceName = hasNextPage ? 'Previous page balance' : 'Initial Balance';
      } else {
        balanceName = 'Balance';
      }

      const balanceRow = statementAccount.flow_type === "facilita_cash_out"
        ? [recordDay, balanceName, '', utils.currencyToLocal(balance, statementAccount.currency)]
        : [recordDay, balanceName, '', '', '', '', utils.currencyToLocal(balance, statementAccount.currency)]

      rows.push(balanceRow)
    }
  })

  const balanceName = hasPreviousPage === false ? 'Final Balance' : 'Balance'

  let initialDate = utils.dbDateTimeToHumanDate(recordStatement[0].inserted_at)

  const initialBalanceRow = statementAccount.flow_type === "facilita_cash_out"
    ? [initialDate, balanceName, '', utils.currencyToLocal(balance_final, statementAccount.currency)]
    : [initialDate, balanceName, '', '', '', '', utils.currencyToLocal(balance_final, statementAccount.currency)]

  rows.unshift(initialBalanceRow)

  return {rows: rows, balance: balance}
}

function parseCurrency(value) {
  if (typeof value !== 'string') return value;

  let cleaned = value.replace(/[^\d.,-]/g, '');

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  const lastSep = Math.max(lastComma, lastDot);

  let integerPart = cleaned.slice(0, lastSep).replace(/[.,]/g, '');
  let decimalPart = cleaned.slice(lastSep + 1);

  if (lastSep === -1) {
    return parseFloat(cleaned.replace(/[^\d-]/g, ''));
  }

  const normalized = `${integerPart}.${decimalPart}`;
  return parseFloat(normalized);
}

export default { exportAndDownloadCsv, exportAndDownloadTransactionsCsv, exchangeRate, statementValue, statementExchangedValue, hasExchangeValue, exportAndDownloadPdf, buildStatementRowsWithBalance }



// WEBPACK FOOTER //
// ./src/shared/Statement.js